import React from 'react';
import type { ChatStep } from '../../types/stream.types';
import { MarkdownMessage } from '../molecules/markdown/MarkdownMessage';
import { ChatLoading } from './chat-loading';
import { AgentActivityGroup } from './agent-activity-group';
import {
  filterRelevantChatSteps,
  mergeProseMessages,
  partitionChatStepsIntoSegments,
} from './utils/step-segments';

interface ChatStepsProps {
  steps: ChatStep[];
  isStreaming?: boolean;
  onViewDocument?: (url: string, name: string) => void;
}

export function ChatSteps({ steps, isStreaming = false, onViewDocument }: ChatStepsProps) {
  const relevantSteps = filterRelevantChatSteps(steps);
  const hasSteps = relevantSteps.length > 0;

  if (!hasSteps && !isStreaming) return null;

  const lastStep = relevantSteps[relevantSteps.length - 1];
  const lastStepIsRunningTool = lastStep?.type === 'tool_call' && !lastStep.output;
  const showLoading = isStreaming && !lastStepIsRunningTool;

  const segments = partitionChatStepsIntoSegments(relevantSteps);

  return (
    <div className="flex flex-col gap-3 w-full select-none">
      {segments.map((segment, segIdx) => {
        const isLastSegment = segIdx === segments.length - 1;

        if (segment.kind === 'prose') {
          const text = mergeProseMessages(segment.steps);
          const firstOrder = segment.steps[0]?.order ?? segIdx;
          return (
            <div
              key={`prose-${firstOrder}-${segIdx}`}
              className="text-slate-800 text-[14px] md:text-[15px] leading-relaxed overflow-x-hidden break-words w-full"
            >
              <MarkdownMessage
                content={text}
                isStreaming={isStreaming && isLastSegment}
                fontSize="text-[14px] md:text-[15px]"
                textColor="text-slate-800"
                onViewDocument={onViewDocument}
              />
            </div>
          );
        }

        return (
          <AgentActivityGroup
            key={`activity-${segment.steps[0]?.order ?? segIdx}-${segIdx}`}
            steps={segment.steps}
            lastTimelineStep={lastStep}
            isStreaming={isStreaming}
            startExpanded={isStreaming && isLastSegment}
            onViewDocument={onViewDocument}
          />
        );
      })}

      {showLoading && (
        <div className="animate-in fade-in slide-in-from-top-1 duration-300 fill-mode-forwards">
          <ChatLoading />
        </div>
      )}
    </div>
  );
}
